import { useState, useRef, useEffect } from "react";
import { Plus, Pencil, Trash2, ChevronDown, X, Search } from "lucide-react";
import moment from "moment";
import {
    useGetGeofences,
    useCreateGeofence,
    useUpdateGeofence,
    useDeleteGeofence,
    useGetGeofenceEvents,
    useGetGeofenceEligibleUsers,
} from "../../../../api/routes/geofences";

interface GeofencePanelProps {
    deviceId: string;
    onStartDraw: (mode: "circle" | "polygon") => void;
    drawnGeofence: {
        type: "circle" | "polygon";
        center?: { lat: number; lng: number };
        radius?: number;
        coordinates?: { lat: number; lng: number }[];
    } | null;
    onClearDrawn: () => void;
}

interface AlertRecipient {
    userId: string;
    alertOnEnter: boolean;
    alertOnExit: boolean;
}

interface GeofenceFormData {
    name: string;
    alertRecipients: AlertRecipient[];
    expiresAt: string | null;
}

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (val: boolean) => void }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-300"}`}
    >
        <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${checked ? "translate-x-[18px]" : "translate-x-[2px]"}`}
        />
    </button>
);

const GeofenceForm = ({
    initial,
    onSave,
    onCancel,
    isSaving,
    deviceId,
}: {
    initial?: GeofenceFormData;
    onSave: (data: GeofenceFormData) => void;
    onCancel: () => void;
    isSaving: boolean;
    deviceId: string;
}) => {
    const [name, setName] = useState(initial?.name || "");
    const [expiresAt, setExpiresAt] = useState(initial?.expiresAt || "");
    const { data: eligibleUsers } = useGetGeofenceEligibleUsers(deviceId);

    const [recipients, setRecipients] = useState<Record<string, { alertOnEnter: boolean; alertOnExit: boolean }>>(() => {
        const map: Record<string, { alertOnEnter: boolean; alertOnExit: boolean }> = {};
        for (const r of initial?.alertRecipients || []) {
            map[r.userId] = { alertOnEnter: r.alertOnEnter, alertOnExit: r.alertOnExit };
        }
        return map;
    });
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const addRecipient = (userId: string) => {
        setRecipients((prev) => ({ ...prev, [userId]: { alertOnEnter: true, alertOnExit: true } }));
        setSearch("");
        setShowDropdown(false);
    };

    const removeRecipient = (userId: string) => {
        setRecipients((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
        });
    };

    const toggleRecipient = (userId: string, field: "alertOnEnter" | "alertOnExit") => {
        setRecipients((prev) => {
            const current = prev[userId] || { alertOnEnter: false, alertOnExit: false };
            return { ...prev, [userId]: { ...current, [field]: !current[field] } };
        });
    };

    const availableUsers = (eligibleUsers || []).filter(
        (u: any) => !recipients[u.id] && (
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
            u.email.toLowerCase().includes(search.toLowerCase())
        )
    );

    const selectedUsers = (eligibleUsers || []).filter((u: any) => recipients[u.id]);

    const handleSave = () => {
        const alertRecipients: AlertRecipient[] = [];
        for (const [userId, prefs] of Object.entries(recipients)) {
            if (prefs.alertOnEnter || prefs.alertOnExit) {
                alertRecipients.push({ userId, ...prefs });
            }
        }
        onSave({ name, alertRecipients, expiresAt: expiresAt || null });
    };

    return (
        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
            <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Geofence name"
                    className="mt-1 block w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
            </div>
            <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Alert Recipients</label>
                {/* Search to add */}
                <div ref={searchRef} className="relative mt-1.5">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                            onFocus={() => setShowDropdown(true)}
                            placeholder="Search users to add..."
                            className="block w-full pl-8 pr-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        />
                    </div>
                    {showDropdown && availableUsers.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-40 overflow-y-auto">
                            {availableUsers.map((user: any) => (
                                <button
                                    key={user.id}
                                    type="button"
                                    onClick={() => addRecipient(user.id)}
                                    className="w-full text-left px-3 py-2 hover:bg-muted"
                                >
                                    <span className="text-sm text-foreground">{user.firstName} {user.lastName}</span>
                                    <span className="text-xs text-muted-foreground ml-2">{user.email}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Selected recipients */}
                {selectedUsers.length > 0 ? (
                    <div className="mt-2 space-y-1.5">
                        {selectedUsers.map((user: any) => {
                            const prefs = recipients[user.id];
                            return (
                                <div key={user.id} className="flex items-center gap-2 p-2 border rounded-md bg-background">
                                    <div className="min-w-0 flex-1">
                                        <span className="text-sm text-foreground truncate block">
                                            {user.firstName} {user.lastName}
                                        </span>
                                        <span className="text-xs text-muted-foreground truncate block">{user.email}</span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-muted-foreground uppercase">Enter</span>
                                            <Toggle checked={prefs.alertOnEnter} onChange={() => toggleRecipient(user.id, "alertOnEnter")} />
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-muted-foreground uppercase">Exit</span>
                                            <Toggle checked={prefs.alertOnExit} onChange={() => toggleRecipient(user.id, "alertOnExit")} />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeRecipient(user.id)}
                                            className="p-0.5 text-muted-foreground hover:text-red-600"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="mt-2 text-xs text-muted-foreground">No recipients added. Search above to add users.</p>
                )}
            </div>
            <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Expiry Date</label>
                <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="mt-1 block w-full px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                />
                <p className="mt-1 text-xs text-muted-foreground">Leave empty for no expiry. Geofence will be deleted after this date.</p>
            </div>
            <div className="flex gap-2 pt-1">
                <button
                    onClick={handleSave}
                    disabled={!name.trim() || isSaving}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm font-medium text-muted-foreground bg-muted rounded-md hover:bg-muted/80"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

const GeofencePanel = ({ deviceId, onStartDraw, drawnGeofence, onClearDrawn }: GeofencePanelProps) => {
    const { data: geofences, isLoading } = useGetGeofences(deviceId);
    const { data: events } = useGetGeofenceEvents(deviceId);
    const createGeofence = useCreateGeofence();
    const updateGeofence = useUpdateGeofence();
    const deleteGeofence = useDeleteGeofence();

    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [showAddDropdown, setShowAddDropdown] = useState(false);

    const handleCreate = (formData: GeofenceFormData) => {
        if (!drawnGeofence) return;
        createGeofence.mutate(
            {
                deviceId,
                data: {
                    ...formData,
                    type: drawnGeofence.type,
                    center: drawnGeofence.center,
                    radius: drawnGeofence.radius,
                    coordinates: drawnGeofence.coordinates,
                    active: true,
                },
            },
            { onSuccess: () => onClearDrawn() }
        );
    };

    const handleUpdate = (geofenceId: string, formData: GeofenceFormData) => {
        updateGeofence.mutate(
            { deviceId, geofenceId, data: formData },
            { onSuccess: () => setEditingId(null) }
        );
    };

    const handleDelete = (geofenceId: string) => {
        deleteGeofence.mutate({ deviceId, geofenceId }, { onSuccess: () => setDeletingId(null) });
    };

    const handleToggleActive = (geofence: any) => {
        updateGeofence.mutate({
            deviceId,
            geofenceId: geofence.id,
            data: { active: !geofence.active },
        });
    };

    return (
        <div className="rounded-lg border bg-card">
            <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Geofences</h3>
                <div className="relative">
                    <button
                        onClick={() => setShowAddDropdown(!showAddDropdown)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Add Geofence
                        <ChevronDown className="w-3 h-3" />
                    </button>
                    {showAddDropdown && (
                        <div className="absolute right-0 mt-1 w-36 bg-popover border rounded-md shadow-lg z-10">
                            <button
                                onClick={() => {
                                    onStartDraw("circle");
                                    setShowAddDropdown(false);
                                }}
                                className="block w-full text-left px-3 py-2 text-sm hover:bg-muted"
                            >
                                Circle
                            </button>
                            <button
                                onClick={() => {
                                    onStartDraw("polygon");
                                    setShowAddDropdown(false);
                                }}
                                className="block w-full text-left px-3 py-2 text-sm hover:bg-muted"
                            >
                                Polygon
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="p-6 space-y-4">
                {drawnGeofence && (
                    <div>
                        <p className="text-sm font-medium text-foreground mb-2">New {drawnGeofence.type} geofence</p>
                        <GeofenceForm
                            onSave={handleCreate}
                            onCancel={onClearDrawn}
                            isSaving={createGeofence.isPending}
                            deviceId={deviceId}
                        />
                    </div>
                )}

                {isLoading ? (
                    <div className="animate-pulse space-y-3">
                        <div className="h-12 bg-muted rounded" />
                        <div className="h-12 bg-muted rounded" />
                    </div>
                ) : (geofences || []).length === 0 && !drawnGeofence ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                        No geofences yet. Click "Add Geofence" to create one.
                    </p>
                ) : (
                    (geofences || []).map((geofence: any) => (
                        <div key={geofence.id} className="border rounded-lg p-3">
                            {editingId === geofence.id ? (
                                <GeofenceForm
                                    initial={{
                                        name: geofence.name,
                                        alertRecipients: geofence.alertRecipients || [],
                                        expiresAt: geofence.expiresAt ? geofence.expiresAt.split("T")[0] : null,
                                    }}
                                    onSave={(data) => handleUpdate(geofence.id, data)}
                                    onCancel={() => setEditingId(null)}
                                    isSaving={updateGeofence.isPending}
                                    deviceId={deviceId}
                                />
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-foreground">{geofence.name}</span>
                                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-muted text-muted-foreground rounded-full">
                                            {geofence.type}
                                        </span>
                                        {geofence.expiresAt && (
                                            <span className="text-[10px] text-muted-foreground">
                                                Expires {moment(geofence.expiresAt).format("MMM D, YYYY")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Toggle
                                            checked={geofence.active !== false}
                                            onChange={() => handleToggleActive(geofence)}
                                        />
                                        <button
                                            onClick={() => setEditingId(geofence.id)}
                                            className="p-1 text-muted-foreground hover:text-foreground"
                                        >
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        {deletingId === geofence.id ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleDelete(geofence.id)}
                                                    className="px-2 py-0.5 text-xs text-white bg-red-600 rounded hover:bg-red-700"
                                                >
                                                    Confirm
                                                </button>
                                                <button
                                                    onClick={() => setDeletingId(null)}
                                                    className="px-2 py-0.5 text-xs text-muted-foreground bg-muted rounded hover:bg-muted/80"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeletingId(geofence.id)}
                                                className="p-1 text-muted-foreground hover:text-red-600"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Recent Events */}
            <div className="px-6 py-4 border-t">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Recent Events</h4>
                {!events || events.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">No geofence events yet.</p>
                ) : (
                    <div className="space-y-2">
                        {events.slice(0, 10).map((event: any, index: number) => (
                            <div key={event.id || index} className="flex items-center gap-3 text-sm">
                                <span
                                    className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded-full ${
                                        event.eventType === "enter"
                                            ? "bg-green-100 text-green-800"
                                            : "bg-red-100 text-red-800"
                                    }`}
                                >
                                    {event.eventType === "enter" ? "Enter" : "Exit"}
                                </span>
                                <span className="text-foreground font-medium truncate">{event.geofence?.name || "Unknown"}</span>
                                <span className="text-muted-foreground text-xs ml-auto whitespace-nowrap">
                                    {moment(Number(event.timestamp)).format("D MMM YYYY, h:mm:ss A")}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GeofencePanel;